import {Module} from "vuex";
import {AsyncJavaRequest, Main} from "@/controller/main";
import {CurrencyState} from "@/store/CurrencyStore";
import componentEdit from "@/views/componentEdit.vue";


interface ToolCreationSetup {
    tool: SimpleToolLine,
    component: SimpleComponentLine,
    position: number
}

export class ComponentEditState {
    public existingStructure: {
        projects: SimpleProjectLine[],
        companies: SimpleCompanyLine[]
    } = {projects: [], companies: []};

    public showEditFrame = false;
    public activeComponentId: number | null = null;
    activeComponent: SimpleComponentLine | null = null;
    selectablePersons: SimplePersonLine[] = [];
    accessPersons: SimplePersonLine[] = [];
    addablePersonObjects: SimplePersonLine[] = [];
    componentTypes: SimpleComponentTypeLine[] | null = null;
    allPersons: SimplePersonLine[] | null = null;
    allCompanyNames: string[] = [];
    moveComponentMode: boolean = false;
}


class SimpleComponentTypeLine {
    id?: number;
    name?: string;
}

class SimpleProjectLine {
    name: string = "";
    children: SimpleComponentLine[] = [];
    projektId?: number;

}

class SimpleCompanyLine {
    firmaId?: number;
    name?: string;
    strasse?: string;
    nummer?: string;
    plz?: string;
    ort?: string;
    land?: string;
    email?: string;
    vendorId?: string;
    shorthand?: string;
    contractPersonId?: number;
    personList?: SimplePersonLine[];
}

class SimplePersonLine {
    personId?: number;
    fullName?: string;
    companyName?: string;
    login?: string;
}

export class SimpleComponentLine {
    type: string = "ib";
    ib: string = "";
    componentId: number | undefined;
    projectId: number | undefined;
    parentId: number | undefined;
    supplierId: number | undefined;
    contractPersonId: number | undefined;
    contractCompanyId: number | undefined;
    throughputTime: number | undefined;
    componentTypeId: number | undefined;
    name: string | undefined;
    description: string | undefined;
    contractPersonFullName: string | undefined;
    partNumber: string | undefined;
    inventoryNumbers: string | undefined;
    supplierName: string | undefined;
    children: SimpleComponentLine[] = [];
    report: boolean = true;
    usedCurrency: string | undefined;
    accessPersonsIds: number[] = [];
}

class SimpleToolLine {
    werkzeugId: number | undefined;
    nummer: string | undefined;
    name: string | undefined;
    beschreibung: string | undefined;
    bauteilId: number | undefined;
    laufendenummer: number | undefined;
    istaktiv: boolean = true;
}

export class ComponentEdittStore implements Module<any, unknown> {
    public namespaced = true;
    public state: ComponentEditState = new ComponentEditState();
    public getters: { [func: string]: (state: ComponentEditState) => any } = {
        existingStructure(state: ComponentEditState) {
            return state.existingStructure;
        },
        showEditFrame(state: ComponentEditState) {
            return state.showEditFrame;
        },
        activeComponent(state: ComponentEditState): SimpleComponentLine | null {
            return state.activeComponent;
        },
        selectablePersons(state: ComponentEditState): SimplePersonLine[] | undefined {
            return state.selectablePersons;
        },
        componentTypes(state: ComponentEditState): SimpleComponentTypeLine[] | null {
            return state.componentTypes;
        },
        accessPersons(state: ComponentEditState): SimplePersonLine[] | null {
            return state.accessPersons;
        },
        allPersons(state: ComponentEditState): SimplePersonLine[] | undefined {
            return state.addablePersonObjects;
        },
        activeComponentId(state: ComponentEditState): number | null {
            return state.activeComponentId;
        },
        moveComponentMode(state: ComponentEditState): boolean {
            return state.moveComponentMode;
        },


    };
    public mutations = {
        showEditFrame(state: ComponentEditState, show: boolean) {
            state.showEditFrame = show;
        },
        async editComponent(state: ComponentEditState, input:{componentId: string,projectId:string}) {
            let {componentId,projectId} = input;
            if (state.moveComponentMode) {
                const isCyclicMove: (current: number, parent: SimpleComponentLine) => void = function (currentId: number, parent: SimpleComponentLine) {
                    if (currentId === parent.componentId) {
                        return true;
                    }
                    if (parent.parentId) {
                        const nxtParent = getComponentById(state.existingStructure.projects, parent.parentId);
                        if (nxtParent && nxtParent.componentId) {
                            return isCyclicMove(currentId, nxtParent);
                        }
                    }
                }

                state.moveComponentMode = false;
                let newParent;
                if(componentId) {
                   newParent = getComponentById(state.existingStructure.projects, Number.parseInt(componentId));
                }
                else{
                    for (let project of state.existingStructure.projects) {
                        if(project.projektId === Number.parseInt(projectId)){
                            newParent = project;
                            break;
                        }
                    }
                }
                if( newParent instanceof SimpleComponentLine &&
                    state.activeComponent?.componentId &&
                    newParent?.componentId &&
                    isCyclicMove(state.activeComponent?.componentId, newParent )){
                    alert("Cyclic component move is not possible");
                    return;
                }

                if (state.activeComponent) {


                    newParent?.children.unshift(state.activeComponent);
                    const oldParentId = state.activeComponent.parentId;
                    const oldProjectId = state.activeComponent.projectId;
                    state.activeComponent.parentId = Number.parseInt(componentId);
                    state.activeComponent.projectId = Number.parseInt(projectId);
                    if (oldParentId || oldProjectId) {
                        let oldParent;
                        if(oldParentId) {
                          oldParent = getComponentById(state.existingStructure.projects, oldParentId);
                        }
                        else{
                            for (let project of state.existingStructure.projects) {
                                if(project.projektId === oldProjectId){
                                    oldParent = project;
                                    break;
                                }
                            }
                        }


                        if (oldParent) {
                            for (let i = 0; i < oldParent.children.length; i++) {
                                let oldParentChild = oldParent.children[i];
                                if (oldParentChild.componentId === state.activeComponent.componentId) {
                                    oldParent.children.splice(i, 1);
                                    break;
                                }
                            }

                        }
                    }
                }

                return;
            }

            state.moveComponentMode = false;
            state.showEditFrame = true;
            state.activeComponentId = Number.parseInt(componentId);
            state.activeComponent = getComponentById(state.existingStructure.projects, Number.parseInt(componentId));
            componentEditStore.mutations.setActiveSupplier(state, state.activeComponent?.supplierId);
            state.accessPersons.splice(0);

            if (state.activeComponent) {
                if (state.allPersons) {
                    state.addablePersonObjects = state.allPersons?.slice();
                }

                state.activeComponent.accessPersonsIds = [];
                const access = await Main.postMessage(new AsyncJavaRequest("call", "getUserAccessIds", componentId));
                state.activeComponent.accessPersonsIds = access;

                for (let accPersId of state.activeComponent.accessPersonsIds) {
                    componentEditStore.mutations.addAccessPerson(state, accPersId);
                }
            }

            state.accessPersons.sort((o1: any, o2: any) => {
                if (o1 === undefined && o2 === undefined) {
                    return 0;
                }
                if (o1.companyName < o2.companyName)
                    return -1;
                if (o1.companyName > o2.companyName)
                    return 1;

                if (o1.fullName < o2.fullName)
                    return -1;
                if (o1.fullName > o2.fullName)
                    return 1;

                if (o1.login < o2.login)
                    return -1;
                if (o1.login > o2.login)
                    return 1;

                return 0;
            });

        },
        addAccessPerson(state: ComponentEditState, personId: number) {
            if (state.addablePersonObjects !== null) {
                for (let i = 0; i < state.addablePersonObjects.length; i++) {
                    let aPerson = state.addablePersonObjects[i];
                    if (aPerson.personId === personId) {
                        state.accessPersons.push(aPerson);
                        state.addablePersonObjects.splice(i, 1);
                        break;
                    }
                }
            }
        },
        removeAccessPerson(state: ComponentEditState, personId: number) {
            if (state.addablePersonObjects !== null) {
                for (let i = 0; i < state.accessPersons.length; i++) {
                    let aPerson = state.accessPersons[i];
                    if (aPerson.personId === personId) {
                        state.addablePersonObjects.push(aPerson);
                        state.accessPersons.splice(i, 1);
                        break;
                    }
                }
            }
        },
        setActiveSupplier(state: ComponentEditState, supplierId: number | undefined) {
            state.selectablePersons.splice(0);
            const addedPersons:{[id:number]:SimplePersonLine} = {};
            if (state.activeComponent) {
                state.activeComponent.supplierId = supplierId;
                state.activeComponent.contractCompanyId = supplierId;
                for (let company of state.existingStructure.companies) {
                    if (company.firmaId === state.activeComponent.supplierId) {
                        if (company.personList) {
                            for (let p of company.personList) {
                                addedPersons[<any>p.personId] = p;
                            }
                            // if (state.selectablePersons.length > 0) {
                            //     componentEditStore.mutations.setActiveContractPerson(state, state.selectablePersons[0].personId);
                            // }
                        }
                        break;
                    }
                }
            }

            for (let pKey in addedPersons) {
                state.selectablePersons.push(addedPersons[pKey]);
            }
        },
        setActiveContractPerson(state: ComponentEditState, contactId: number | undefined) {
            if (state.activeComponent) {
                state.activeComponent.contractPersonId = contactId;
            }
        },

        setActiveComponentType(state: ComponentEditState, componentTypeId: number | undefined) {
            if (state.activeComponent) {
                state.activeComponent.componentTypeId = componentTypeId;
            }
        },
        async saveComponent(state: ComponentEditState) {
            if (state.activeComponent) {
                state.activeComponent.accessPersonsIds = [];
                for (let accessPerson of state.accessPersons) {
                    if (accessPerson.personId) {
                        state.activeComponent.accessPersonsIds.push(accessPerson.personId);
                    }
                }

                const data:SimpleComponentLine = await Main.postMessage(new AsyncJavaRequest("call", "saveComponent",
                    JSON.stringify(state.activeComponent)));

                console.log(data);
                if (state.activeComponent.componentId) {
                    await componentEditStore.mutations.editComponent(state, {componentId:"" + state.activeComponent.componentId,projectId:"" + state.activeComponent.projectId});
                }
            }
        },

        async startMoveComponent(state: ComponentEditState) {
            state.moveComponentMode = !state.moveComponentMode;
        },

        async loadFromDb(state: ComponentEditState) {
            const data = await Main.postMessage(new AsyncJavaRequest("call", "allComponents"));
            state.existingStructure = data;

            const types = await Main.postMessage(new AsyncJavaRequest("call", "allComponentTypes"));
            state.componentTypes = types;

            const persons = await Main.postMessage(new AsyncJavaRequest("call", "allPersons"));
            state.allPersons = persons;

            const companyNames = new Set<string>();
            if (state.allPersons) {
                for (let aPerson of state.allPersons) {
                    companyNames.add(aPerson.companyName || "");
                }

                const nameArray = Array.from(companyNames.values());
                nameArray.sort();
                state.allCompanyNames = nameArray;
            }


            // if (state.existingStructure.projects.length > 0) {
            //     state.selectedProject = (<any>state.existingStructure.projects)[0];
            // }
            //
            // const orgstate = new ImportConfigStates();
            // for (let key of Object.keys(orgstate)) {
            //     const res = await <IDBase><any>QueryFacade.get("import_config", key);
            //     (<any>state).importConfigStates[key] = (<any>orgstate)[key];
            //     if (res) {
            //         (<any>state).importConfigStates[res.id] = res;
            //     }
            // }
        }
    };
}

function getComponentById(toSearch: SimpleComponentLine[] | SimpleProjectLine[], componentId: number): SimpleComponentLine | null {

    for (let aComponent of toSearch) {
        if ((<any>aComponent)["componentId"]) {
            if ((<SimpleComponentLine>aComponent).componentId && (<SimpleComponentLine>aComponent).componentId == componentId) {
                return (<SimpleComponentLine>aComponent);
            }
        }

        if (aComponent.children) {
            const resFromChildren = getComponentById(aComponent.children, componentId);
            if (resFromChildren !== null) {
                return resFromChildren;
            }
        }

    }

    return null;
}


export const componentEditStore = new ComponentEdittStore();